import React from 'react';
import { CalendarMonth } from '@mui/icons-material';
import api from '../../api/axios';
import {
  Box,
  Collapse,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Skeleton,
  Stack,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Event, Schedule, PendingActions, ChevronLeft, ChevronRight } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { useAuth } from '../../context/AuthContext';
import { useReservas } from '../../hooks/useReservas';
import { getMyPendings, getPendingCounts } from '../../api/firmas';
import ReservaDialog from '../../components/ReservaDialog';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from '../../styles/theme'; // ajusta la ruta si es distinto

/*  Imports para el calendario */
import { Badge } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, PickersDay, PickersDayProps } from '@mui/x-date-pickers';

/* ⬇️ Botones de ticketing */
import {
  TeacherDelayButton,
  StudentAbsenceButton,
  StudentDelayButton, //
} from '../../components/TicketButtons';

import { ReactComponent as IconoAlumn } from '../../imagenes/Iconos/Iconos_botones/iconoalumno.svg';
import gorilaCabreado from '../../imagenes/Gorilas/gorila_cabreado.png';

/* CALENDARIO DASHBOARD */
function SemanaHorasKPIs() {
  const { user } = useAuth();
  const profesorId = user?.id;

  const weekStart = React.useMemo(() => startOfISOWeek(dayjs()), []);
  const weekEnd = React.useMemo(() => weekStart.add(6, 'day').endOf('day'), [weekStart]);
  const now = dayjs();

  const { rows = [], loading } = useReservas(fmtDate(weekStart), fmtDate(weekEnd), {
    profesorId,
  } as any);

  // Filtrado base: dentro de la semana, no canceladas, no “pendiente de reagendar”, y que computen en horas
  const base = React.useMemo(
    () =>
      rows
        .filter((r) => {
          const s = getStart(r);
          return s ? s.isBetween(weekStart, weekEnd, 'millisecond', '[]') : false;
        })
        .filter((r) => !isCancelled(r))
        .filter((r) => !isPendienteReagendar(r))
        .filter(countsInTeachingHours),
    [rows, weekStart, weekEnd],
  );

  // Criterio:
  // - Trabajadas: clases que ya han terminado (fin <= now)
  // - Pendientes: clases que aún no han empezado (inicio > now)
  //   (Las que están en curso no cuentan en ninguna para no “inflar” ambas)
  const horasTrabajadas = React.useMemo(
    () =>
      base
        .filter((r) => {
          const e = getEnd(r);
          return e ? !e.isAfter(now) : false;
        })
        .reduce((acc, r) => acc + durHours(r), 0),
    [base, now],
  );

  const horasPendientes = React.useMemo(
    () =>
      base
        .filter((r) => {
          const s = getStart(r);
          return s ? s.isAfter(now) : false;
        })
        .reduce((acc, r) => acc + durHours(r), 0),
    [base, now],
  );

  return (
    <Stack spacing={2}>
      <Card elevation={3} sx={{ p: 2, bgcolor: '#ff7f11', color: 'white', borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Horas trabajadas (semana)
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={80} height={48} />
        ) : (
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
            {horasTrabajadas.toFixed(1)}{' '}
            <Typography component="span" variant="h6" sx={{ ml: 0.5 }}>
              h
            </Typography>
          </Typography>
        )}
      </Card>

      <Card elevation={3} sx={{ p: 2, bgcolor: 'black', color: 'white', borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Horas pendientes (semana)
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={80} height={48} sx={{ bgcolor: 'grey.800' }} />
        ) : (
          <Typography variant="h3" sx={{ fontWeight: 800, mt: 1 }}>
            {horasPendientes.toFixed(1)}{' '}
            <Typography component="span" variant="h6" sx={{ ml: 0.5 }}>
              h
            </Typography>
          </Typography>
        )}
      </Card>
    </Stack>
  );
}
// ======= Calendario mensual que marca días con clases =======
function CalendarClasesCard() {
  const { t } = useTranslation<'translation'>();
  const { user } = useAuth();
  const profesorId = user?.id;

  // Mes visible en el calendario
  const [month, setMonth] = React.useState<Dayjs>(dayjs().startOf('month'));

  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');

  // Traemos las reservas SOLO del mes visible
  const { rows = [], loading } = useReservas(
    monthStart.format('YYYY-MM-DD'),
    monthEnd.format('YYYY-MM-DD'),
    { profesorId } as any,
  );

  // Conjunto/contador por día del mes
  const daysWithClasses = React.useMemo(() => {
    const map = new Map<string, number>();
    rows
      .filter((r) => !isCancelled(r) && !isPendienteReagendar(r))
      .forEach((r) => {
        const s = getStart(r);
        if (!s) return;
        const key = s.format('YYYY-MM-DD');
        map.set(key, (map.get(key) ?? 0) + 1);
      });
    return map;
  }, [rows]);

  // Renderizado del día con punto/indicador si hay clases
  const DiaConClases = (props: PickersDayProps<Dayjs>) => {
    const { day, outsideCurrentMonth, onMouseDown, onClick, ...other } = props;
    const key = day.format('YYYY-MM-DD');
    const hasClassToday = !outsideCurrentMonth && (daysWithClasses.get(key) ?? 0) > 0;

    const stopSelect: React.MouseEventHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        disableRipple
        tabIndex={-1}
        onMouseDown={stopSelect}
        onClick={stopSelect}
        sx={{
          color: '#fff',
          cursor: 'default',
          pointerEvents: 'none',
          ...(hasClassToday && {
            backgroundColor: '#F5DE58',
            color: '#000000ff',
            borderRadius: '50%',
            width: 32,
            height: 32,
            lineHeight: '32px',
            fontSize: '0.875rem',
            '&:hover': { backgroundColor: '#F5DE58' },
          }),
        }}
      />
    );
  };

  return (
    <Card elevation={0} variant="outlined">
      <CardContent sx={{ pt: 2, bgcolor: '#21A598', borderRadius: 1 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs} adapterLocale="es">
          <DateCalendar
            value={null}
            onMonthChange={(newMonth) => setMonth(newMonth.startOf('month'))}
            onChange={() => {}}
            loading={loading}
            views={['day']}
            reduceAnimations
            slots={{ day: DiaConClases }}
            sx={{
              color: '#fff',
              '& .MuiPickersCalendarHeader-label': {
                color: '#fff',
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: '#fff',
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: '#fff',
              },
            }}
          />
        </LocalizationProvider>
      </CardContent>
    </Card>
  );
}

/* DESPLEGABLE DE RETRASOS*/
function DelayInlinePanel({
  open,
  onClose,
  reservaId,
  hasStarted,
}: {
  open: boolean;
  onClose: () => void;
  reservaId: number;
  hasStarted: boolean;
}) {
  return (
    <Collapse in={open} timeout={200} unmountOnExit>
      <Box
        sx={{
          mt: 1,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{
            '& button': {
              height: 36,
              minWidth: 'auto',
              textTransform: 'none',
            },
            '& .MuiSvgIcon-root': {
              width: 20,
              height: 20,
            },
          }}
        >
          {/* PROFESOR */}
          <Box sx={{ '& .MuiButton-startIcon': { mr: 1 } }}>
            <TeacherDelayButton
              reservaId={reservaId}
              variant="outlined"
              size="small"
              buttonText="Profesor"
            />
          </Box>

          {/* ALUMNO */}
          <Tooltip
            title={!hasStarted ? 'Disponible cuando comience la clase' : ''}
            disableHoverListener={hasStarted}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 36,
                px: 1.25,
                border: '1px solid #21a598',
                borderRadius: '4px',
                color: '#21a598',
                cursor: 'pointer',
                textTransform: 'none',
                pointerEvents: hasStarted ? 'auto' : 'none',
                opacity: hasStarted ? 1 : 0.45,
                '&:hover': {
                  backgroundColor: hasStarted ? 'rgba(33,165,152,0.08)' : 'transparent',
                  borderColor: hasStarted ? '#1b887f' : 'divider',
                  color: hasStarted ? '#1b887f' : '#21a598',
                },
                '& > button': {
                  p: 0,
                  minWidth: 'auto',
                  border: 'none',
                  background: 'transparent',
                  color: 'inherit',
                },
                '& .MuiButton-startIcon, & .MuiButton-endIcon, & button svg': {
                  display: 'none !important',
                },
              }}
            >
              <Box component={IconoAlumn} sx={{ width: 20, height: 20, mr: 0.75 }} />
              <StudentDelayButton
                reservaId={reservaId}
                variant="text"
                size="small"
                buttonText="Alumno"
              />
            </Box>
          </Tooltip>
        </Stack>
      </Box>
    </Collapse>
  );
}

function DelayToggleButton({
  open,
  onToggle,
  hideIcon = false, // 🔹 nueva prop con valor por defecto
}: {
  open: boolean;
  onToggle: () => void;
  hideIcon?: boolean; // 🔹 añadida al tipo
}) {
  return (
    <Button
      variant="outlined"
      size="small"
      onClick={onToggle}
      sx={{
        color: '#21a598',
        borderColor: '#21a598',
        minWidth: 36,
        height: 36,
        p: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          backgroundColor: 'rgba(33,165,152,0.08)',
          borderColor: '#1b887f',
          color: '#1b887f',
        },
      }}
    >
      <AccessTimeIcon fontSize="small" />
    </Button>
  );
}
/* ====================== util fechas ====================== */
function startOfISOWeek(d: Dayjs = dayjs()) {
  const diff = (d.day() + 6) % 7;
  return d.subtract(diff, 'day').startOf('day');
}
const fmtDate = (v?: string | Dayjs | Date) => (v ? dayjs(v).format('YYYY-MM-DD') : '');
const dateOnly = (f?: string | null) => {
  const s = f ? String(f) : '';
  return s.length >= 10 ? s.slice(0, 10) : s;
};
const HHmm = (s?: string | null) => {
  const v = s ? String(s) : '';
  return v.length >= 5 ? v.slice(0, 5) : v;
};

/** Preferimos siempre ISO (inicio/fin) si están presentes */
function getStart(r: any): Dayjs | null {
  const iso =
    r?.inicio ?? r?.start ?? r?.startDate ?? r?.fechaHoraInicio ?? r?.fecha_hora_inicio ?? null;
  if (iso) {
    const d = dayjs(iso);
    if (d.isValid()) return d;
  }
  const d = dateOnly(r?.fecha ?? r?.date);
  if (!d) return null;
  const hhmm = HHmm(r?.hora_inicio ?? r?.horaInicio);
  const [h, m] = hhmm ? hhmm.split(':').map(Number) : [0, 0];
  return dayjs(`${d}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
}
function getEnd(r: any): Dayjs | null {
  const iso = r?.fin ?? r?.end ?? r?.endDate ?? r?.fechaHoraFin ?? r?.fecha_hora_fin ?? null;
  if (iso) {
    const d = dayjs(iso);
    if (d.isValid()) return d;
  }
  const d = dateOnly(r?.fecha ?? r?.date);
  if (!d) return null;
  const hhmm = HHmm(r?.hora_fin ?? r?.horaFin);
  const [h, m] = hhmm ? hhmm.split(':').map(Number) : [0, 0];
  return dayjs(`${d}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
}

/* ====== estado / cómputo horas ====== */
const isCancelled = (evt: any) => {
  const code = String(evt?.estadoCode ?? '').toUpperCase();
  const label = String(evt?.estado ?? '').toLowerCase();
  return code.startsWith('CANCEL') || label.includes('cancel');
};

/* ✨ NUEVO: detectar “pendiente de reagendar” */
const isPendienteReagendar = (evt: any) => {
  const norm = (s: any) =>
    String(s ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '')
      .toLowerCase();

  const code = norm(evt?.estadoCode);
  const label = norm(evt?.estado);

  const hasReagendar =
    code.includes('reagend') ||
    code.includes('reschedul') ||
    label.includes('reagend') ||
    label.includes('reschedul');

  const hasPendiente = code.includes('pend') || code.includes('pending') || label.includes('pend');

  return hasReagendar && hasPendiente;
};

function countsInTeachingHours(evt: any): boolean {
  const raw = (evt?.estadoCode ?? evt?.estado ?? '').toString().toLowerCase();
  if (!raw) return true;
  return !(
    raw.includes('cancel') ||
    raw.includes('anul') ||
    raw.includes('no show') ||
    raw.includes('ausent')
  );
}
function durHours(evt: any): number {
  const s = getStart(evt);
  const e = getEnd(evt);
  if (!s || !e) return 0;
  return Math.max(0, e.diff(s, 'minute')) / 60;
}

/* ====================== Hook agregador (sin cambios salvo filtro) ====================== */
function useProfesorDashboardData() {
  const { user } = useAuth();
  const profesorId = user?.id;

  const hoy = fmtDate(dayjs());
  const dentro7 = fmtDate(dayjs().add(7, 'day'));
  const weekStart = fmtDate(startOfISOWeek(dayjs()));
  const weekEnd = fmtDate(startOfISOWeek(dayjs()).add(6, 'day'));

  const now = dayjs();
  const limit72h = now.add(72, 'hour');
  const limit7d = now.add(7, 'day').endOf('day');
  const weekStartD = startOfISOWeek(dayjs());
  const weekEndD = weekStartD.add(6, 'day').endOf('day');

  const { rows: rowsHoyRaw = [], loading: loadingHoy } = useReservas(hoy, hoy, {
    profesorId,
  } as any);
  const rowsHoy = React.useMemo(
    () =>
      rowsHoyRaw
        .filter((r) => !isCancelled(r))
        .filter((r) => !isPendienteReagendar(r)) /* ✨ ocultar */
        .filter((r) => {
          const s = getStart(r);
          return s ? s.isSame(now, 'day') : false;
        }),
    [rowsHoyRaw, now],
  );

  const { rows: rowsRango = [], loading: loadingRango } = useReservas(hoy, dentro7, {
    profesorId,
  } as any);
  const proximasAll = React.useMemo(
    () =>
      rowsRango
        .filter((r) => !isCancelled(r))
        .filter((r) => !isPendienteReagendar(r)) /* ✨ ocultar */
        .map((r) => ({ r, s: getStart(r) }))
        .filter((x) => x.s && x.s.isAfter(now) && x.s.valueOf() <= limit7d.valueOf())
        .sort((a, b) => a.s!.valueOf() - b.s!.valueOf())
        .map((x) => x.r),
    [rowsRango, now, limit7d],
  );
  const proximas72h = React.useMemo(
    () =>
      proximasAll.filter((r) => {
        const s = getStart(r);
        return s ? s.valueOf() <= limit72h.valueOf() : false;
      }),
    [proximasAll, limit72h],
  );
  const proximas = React.useMemo(
    () =>
      proximasAll.filter((r) => {
        const s = getStart(r);
        return s ? s.isAfter(now, 'day') : false;
      }),
    [proximasAll, now],
  );

  const { rows: rowsSemanaRaw = [], loading: loadingSemana } = useReservas(weekStart, weekEnd, {
    profesorId,
  } as any);
  const horasSemana = React.useMemo(
    () =>
      rowsSemanaRaw
        .filter((r) => {
          const s = getStart(r);
          return s
            ? s.valueOf() >= weekStartD.valueOf() && s.valueOf() <= weekEndD.valueOf()
            : false;
        })
        .filter(countsInTeachingHours)
        .reduce((acc: number, r: any) => acc + durHours(r), 0),
    [rowsSemanaRaw, weekStartD, weekEndD],
  );

  const qPendientes = useQuery({
    queryKey: ['dashboard', 'mis-pendientes', { profesorId }],
    queryFn: () => getMyPendings({ take: 8 }),
    staleTime: 60_000,
  });
  const qPendCounts = useQuery({
    queryKey: ['dashboard', 'mis-pendientes-counts', { profesorId }],
    queryFn: () => getPendingCounts(),
    staleTime: 60_000,
  });

  return {
    rowsHoy,
    proximas,
    proximas72h,
    horasSemana,
    pendingList: qPendientes.data?.rows ?? [],
    pendingCounts: qPendCounts.data,
    loading: {
      hoy: loadingHoy,
      proximas: loadingRango,
      semana: loadingSemana,
      pendientes: qPendientes.isLoading,
      counts: qPendCounts.isLoading,
    },
  };
}

/* ====================== UI helpers (KPI genérico) ====================== */
function KpiCard(props: {
  icon: React.ReactNode;
  label: string;
  value?: string | number;
  loading?: boolean;
  action?: React.ReactNode;
}) {
  const { icon, label, value, loading, action } = props;

  return (
    <Card elevation={0} variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={1}>
          {/* Título arriba */}
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>

          {/* Icono + Valor en paralelo; acción a la derecha */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>{icon}</Box>

            <Box sx={{ flexGrow: 1 }}>
              {loading ? (
                <Skeleton variant="text" width={80} />
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {value}
                </Typography>
              )}
            </Box>

            {action ? <Box>{action}</Box> : null}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

/*--Función para mensaje de firmas pendientes--*/
function PendingFirmasCard({ count, loading }: { count?: number; loading?: boolean }) {
  const { t } = useTranslation<'translation'>();
  const total = Number(count ?? 0);
  const hasPendings = total > 0;

  return (
    <Card elevation={0} variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        {loading ? (
          <Stack spacing={1}>
            <Skeleton variant="text" width={160} />
            <Skeleton variant="rectangular" height={56} />
          </Stack>
        ) : hasPendings ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            {/* Imagen cuando HAY pendientes */}
            <Box
              component="img"
              src={gorilaCabreado}
              alt="Pendiente de firmas"
              sx={{
                width: 80,
                height: 80,
                objectFit: 'contain',
                flexShrink: 0,
                borderRadius: 2,
                userSelect: 'none',
              }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('profesor.dashboard.heyFirmar', '¡Hey! ¡Tienes que firmar!')}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {t('profesor.dashboard.pendientesX', '{{n}} firmas pendientes', { n: total })}
              </Typography>
            </Box>

            <Button
              size="small"
              component={RouterLink}
              to="/profesor/firmas"
              variant="contained"
              sx={{
                border: '2px solid',
                borderColor: '#1b887f',
                borderRadius: '10px',
                textTransform: 'none',
                px: 2,
                '&:hover': { borderColor: '#146a63' },
              }}
            >
              {t('profesor.dashboard.firmar', 'Firmar')}
            </Button>
          </Stack>
        ) : (
          // Mensaje cuando NO hay pendientes
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              color="success"
              label={t('profesor.dashboard.todoFirmado', '¡Todas tus clases están firmadas!')}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function EstadoChip({ r }: { r: any }) {
  const label = r?.estado ?? r?.estadoCode ?? '—';
  const cancelled = typeof label === 'string' && label.toLowerCase().includes('cancel');

  const chipStyles = cancelled
    ? {
        borderColor: '#ef4d70', // color de borde para outlined
        color: '#ef4d70', // texto
        backgroundColor: 'transparent', // fondo transparente para outlined
      }
    : {
        backgroundColor: '#21a598', // fondo verde personalizado
        color: '#fff', // texto blanco
      };

  return (
    <Chip
      label={label}
      variant={cancelled ? 'outlined' : 'filled'}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.7rem',
        px: 0.3,
        py: 0.1,
        ...chipStyles,
      }}
    />
  );
}

/* ====================== Secciones ====================== */

function ClasesHoyList({
  rows,
  loading,
  onOpenDetails,
}: {
  rows: any[];
  loading?: boolean;
  onOpenDetails?: (row: any) => void;
}) {
  const { t } = useTranslation<'translation'>();

  /* ⬇️ nuevo estado: qué fila está expandida */
  const [expandedId, setExpandedId] = React.useState<number | null>(null);
  const toggle = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <Card elevation={0} variant="outlined">
      <CardHeader title={t('profesor.dashboard.kpiHoy')} />
      <CardContent>
        {loading ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} height={40} />)
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">{t('profesor.dashboard.sinClasesHoy')}</Typography>
        ) : (
          <List dense>
            {rows.map((r: any) => {
              const s = getStart(r);
              const e = getEnd(r);
              const hasStarted = s ? !dayjs().isBefore(s) : false;
              const primary =
                s && e
                  ? `${s.format('YYYY-MM-DD')} · ${s.format('HH:mm')}–${e.format('HH:mm')}`
                  : `${dateOnly(r?.fecha ?? r?.date)} · ${HHmm(r?.hora_inicio ?? r?.horaInicio)}–${HHmm(r?.hora_fin ?? r?.horaFin)}`;

              const alumno = r?.cliente ?? r?.clienteNom ?? r?.nombreCliente ?? r?.alumno ?? '—';
              const asignatura = r?.nombreAsignatura ?? r?.asignatura ?? r?.nombre_asignatura;
              const centro = r?.nombreCentro ?? r?.centro ?? r?.nombre_centro;

              return (
                <React.Fragment key={String(r.id ?? primary)}>
                  <ListItem disablePadding>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ width: '100%', py: 0.5 }}
                    >
                      {/* Contenedor de texto */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                noWrap
                                title={asignatura ? `${asignatura} · ${alumno}` : `${alumno}`}
                              >
                                {asignatura ? `${asignatura} · ` : ''}
                                {alumno}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                minWidth: 0,
                              }}
                            >
                              <EstadoChip r={r} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  flex: '1 1 auto',
                                  minWidth: 0,
                                }}
                              >
                                {primary}
                                {centro ? ` · ${centro}` : ''}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>

                      {/* Contenedor de botones alineados */}

                      <Stack direction="row" spacing={1} flexShrink={0}>
                        {/* 🔽 Botón que abre/cierra el panel inline (empuja contenido) */}
                        <Box sx={{ height: 36, display: 'flex', alignItems: 'center' }}>
                          <DelayToggleButton
                            open={expandedId === Number(r.id)}
                            onToggle={() => toggle(Number(r.id))}
                          />
                        </Box>

                        <Box
                          sx={{
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Tooltip
                            title={!hasStarted ? 'Disponible cuando comience la clase' : ''}
                            disableHoverListener={hasStarted}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: 'auto',
                                alignItems: 'center',
                                opacity: hasStarted ? 1 : 0.5,
                                pointerEvents: hasStarted ? 'auto' : 'none',
                              }}
                            >
                              <StudentAbsenceButton
                                reservaId={Number(r.id)}
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                          </Tooltip>
                        </Box>

                        <Box
                          sx={{
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onOpenDetails?.(r)}
                            sx={{ height: '100%' }}
                          >
                            {t('profesor.dashboard.detalles')}
                          </Button>
                        </Box>
                      </Stack>
                    </Stack>
                  </ListItem>

                  {/* DESPLEGABLE: PROFESOR - ALUMNO */}
                  <Box sx={{ px: 2 }}>
                    <DelayInlinePanel
                      open={expandedId === Number(r.id)}
                      onClose={() => setExpandedId(null)}
                      reservaId={Number(r.id)}
                      hasStarted={hasStarted}
                    />
                  </Box>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}

function ProximasClasesAccordion({
  rows,
  loading,
  onOpenDetails,
}: {
  rows: any[];
  loading?: boolean;
  onOpenDetails?: (row: any) => void; // ⬅️ NUEVO
}) {
  const { t } = useTranslation<'translation'>();
  const [expandedId, setExpandedId] = React.useState<number | null>(null);
  const toggle = (id: number) => setExpandedId((prev) => (prev === id ? null : id));

  // Agrupa por fecha tomando la fecha desde getStart() para evitar desajustes
  const grouped = React.useMemo(() => {
    const map = new Map<string, any[]>();
    rows.forEach((r) => {
      const s = getStart(r);
      const key = s ? s.format('YYYY-MM-DD') : dateOnly(r?.fecha ?? r?.date) || '—';
      (map.get(key) ?? map.set(key, []).get(key)!).push(r);
    });
    return [...map.entries()].sort(([a], [b]) => (a < b ? -1 : 1));
  }, [rows]);

  return (
    <Card elevation={0} variant="outlined">
      <CardHeader title={t('profesor.dashboard.proximas')} />
      <CardContent>
        {loading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} height={40} />
            ))}
          </>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">
            {t('profesor.dashboard.sinProximas', 'No hay clases próximas.')}
          </Typography>
        ) : (
          grouped.map(([fecha, arr]) => (
            <Box key={fecha} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {dayjs(fecha).format('ddd DD MMM')}
              </Typography>
              <List dense>
                {arr
                  .map((r) => ({ r, s: getStart(r), e: getEnd(r) }))
                  .sort((a, b) => (a.s?.valueOf() ?? 0) - (b.s?.valueOf() ?? 0))
                  .map(({ r, s, e }) => {
                    const primary =
                      s && e
                        ? `${s.format('HH:mm')}–${e.format('HH:mm')} · ${
                            r?.nombreAsignatura ?? r?.asignatura ?? r?.nombre_asignatura ?? '—'
                          }`
                        : `${HHmm(r?.hora_inicio ?? r?.horaInicio)}–${HHmm(r?.hora_fin ?? r?.horaFin)}`;
                    const alumno =
                      r?.cliente ?? r?.clienteNom ?? r?.nombreCliente ?? r?.alumno ?? '—';

                    return (
                      <React.Fragment key={String(r.id ?? `${fecha}-${primary}`)}>
                        <ListItem
                          secondaryAction={
                            <Stack direction="row" spacing={1}>
                              {/* Botón Detalles */}
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onOpenDetails?.(r)}
                              >
                                {t('profesor.dashboard.detalles', 'Detalles')}
                              </Button>
                            </Stack>
                          }
                        >
                          <ListItemText
                            primary={primary}
                            secondary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                <EstadoChip r={r} />
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {alumno}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {/* Panel deslizante inline que EMPUJA el resto de la lista */}
                        <Box sx={{ px: 2 }}>
                          <DelayInlinePanel
                            open={expandedId === Number(r.id)}
                            onClose={() => setExpandedId(null)}
                            reservaId={Number(r.id)}
                            hasStarted={false}
                          />
                        </Box>
                        <Divider component="li" />
                      </React.Fragment>
                    );
                  })}
              </List>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function useClasesProgressionRange(start: Dayjs, end: Dayjs) {
  const { user } = useAuth();
  const profesorId = user?.id;

  const startW = startOfISOWeek(start);
  const endW = startOfISOWeek(end).add(6, 'day').endOf('day');

  const weeks = Math.max(1, endW.diff(startW, 'week') + 1);

  const { rows = [], loading } = useReservas(fmtDate(startW), fmtDate(endW), {
    profesorId,
  } as any);

  const counts = React.useMemo(() => {
    const arr = new Array(weeks).fill(0);
    rows.forEach((r: any) => {
      if (isCancelled(r)) return;
      const s = getStart(r);
      if (!s) return;
      if (s.isBefore(startW) || s.isAfter(endW)) return;
      const idx = s.diff(startW, 'week');
      if (idx >= 0 && idx < weeks) arr[idx] += 1;
    });
    return arr;
  }, [rows, weeks, startW, endW]);

  const labels = React.useMemo(
    () =>
      new Array(weeks).fill(0).map((_, i) => {
        const a = startW.add(i, 'week');
        const b = a.add(6, 'day');
        return `${a.format('DD MMM')} – ${b.format('DD MMM')}`;
      }),
    [weeks, startW],
  );

  return { counts, labels, loading, startW, endW };
}

function InteractiveLineChart({
  data,
  labels,
  height = 200,
  color = '#1976d2',
}: {
  data: number[];
  labels: string[];
  height?: number;
  color?: string;
}) {
  const pad = 30;
  const w = 600; // viewBox width (responsive mediante width:100%)
  const h = Math.max(height, 150);
  const maxY = Math.max(1, ...data);
  const stepX = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
  const scaleY = (h - pad * 2) / maxY;

  const pts = data.map((v, i) => [pad + i * stepX, h - pad - v * scaleY]) as [number, number][];

  const d = pts.length > 0 ? 'M ' + pts.map(([x, y]) => `${x} ${y}`).join(' L ') : '';

  const [hover, setHover] = React.useState<number | null>(null);

  const onMove: React.MouseEventHandler<SVGRectElement> = (e) => {
    const rect = (e.currentTarget as SVGRectElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const idx = stepX > 0 ? Math.round((x - pad) / stepX) : 0;
    const clamped = Math.max(0, Math.min(data.length - 1, idx));
    setHover(clamped);
  };

  const onLeave = () => setHover(null);

  return (
    <Box sx={{ position: 'relative' }}>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="Progresión de clases">
        {/* Ejes */}
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#ccc" />
        <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="#ccc" />
        {/* Ticks Y */}
        <text x={pad - 8} y={h - pad + 12} textAnchor="end" fontSize="10" fill="#666">
          0
        </text>
        <text x={pad - 8} y={pad + 4} textAnchor="end" fontSize="10" fill="#666">
          {maxY}
        </text>

        {/* Serie */}
        <path d={d} fill="none" stroke={color} strokeWidth="2.5" />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={hover === i ? 4.5 : 3} fill={color} />
        ))}

        {/* Guía + área capturable */}
        {hover != null && (
          <line
            x1={pts[hover][0]}
            y1={pad}
            x2={pts[hover][0]}
            y2={h - pad}
            stroke="#999"
            strokeDasharray="4 3"
          />
        )}
        <rect
          x={pad}
          y={pad}
          width={w - pad * 2}
          height={h - pad * 2}
          fill="transparent"
          onMouseMove={onMove}
          onMouseLeave={onLeave}
        />
      </svg>

      {/* Tooltip absoluto */}
      {hover != null && (
        <Box
          sx={{
            position: 'absolute',
            left: `${(pts[hover][0] / w) * 100}%`,
            top: `${(pts[hover][1] / h) * 100}%`,
            transform: 'translate(-50%, -120%)',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
            px: 1,
            py: 0.5,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography variant="caption">
            {labels[hover]} — <strong>{data[hover]}</strong>{' '}
            {data[hover] === 1 ? 'clase' : 'clases'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function ClasesProgressionCard() {
  const { t } = useTranslation<'translation'>();

  // Rango por defecto: últimas 8 semanas (como antes)
  const defaultFrom = startOfISOWeek(dayjs().subtract((8 - 1) * 7, 'day'));
  const defaultTo = dayjs();

  const [fromStr, setFromStr] = React.useState(fmtDate(defaultFrom));
  const [toStr, setToStr] = React.useState(fmtDate(defaultTo));
  const [from, setFrom] = React.useState<Dayjs>(defaultFrom);
  const [to, setTo] = React.useState<Dayjs>(defaultTo);

  const apply = () => {
    const s = dayjs(fromStr);
    const e = dayjs(toStr);
    if (s.isValid() && e.isValid() && !e.isBefore(s)) {
      setFrom(s);
      setTo(e);
    }
  };

  const { counts, labels, loading } = useClasesProgressionRange(from, to);

  return (
    <Card elevation={0} variant="outlined">
      <CardHeader
        title={t('profesor.dashboard.progress')}
        subheader={`${from.format('DD MMM')} – ${to.format('DD MMM YYYY')}`}
      />
      <CardContent>
        {/* Filtros: Desde / Hasta */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            mb: 2,
            px: 1, // padding horizontal extra
            py: 0.5, // padding vertical extra
          }}
        >
          <TextField
            size="small"
            type="date"
            label={t('profesor.dashboard.desde', 'Desde')}
            value={fromStr}
            onChange={(e) => setFromStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                minHeight: '40px',
                '&.Mui-focused fieldset': {
                  borderColor: '#21a598', // borde verde
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#21a598', // label verde
              },
              '& .MuiInputBase-input': {
                color: 'black', // texto dentro del input en negro
                padding: '0 10px',
              },
            }}
          />

          <TextField
            size="small"
            type="date"
            label={t('profesor.dashboard.hasta', 'Hasta')}
            value={toStr}
            onChange={(e) => setToStr(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                minHeight: '40px',
                '&.Mui-focused fieldset': {
                  borderColor: '#21a598', // borde verde
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#21a598', // label verde
              },
              '& .MuiInputBase-input': {
                color: 'black', // texto dentro del input en negro
                padding: '0 10px',
              },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            sx={{
              minHeight: '40px',
              px: 3,
              py: 0.25,
              fontSize: '0.875rem',
            }}
            onClick={apply}
          >
            {t('profesor.dashboard.aplicar', 'Aplicar')}
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="caption" color="text.secondary">
            {t('profesor.dashboard.agrupacion', 'Agrupación: semana ISO')}
          </Typography>
        </Stack>

        {loading ? (
          <Skeleton variant="rectangular" height={200} />
        ) : (
          <InteractiveLineChart data={counts} labels={labels} color="#21a598" />
        )}
      </CardContent>
    </Card>
  );
}
/* ========= NUEVO: KPI “Clases por día” con flechas (reemplaza “Próximas 72 h”) ========= */
function ClasesDiaKpi() {
  const { t } = useTranslation<'translation'>();
  const { user } = useAuth();
  const profesorId = user?.id;

  const [dayOffset, setDayOffset] = React.useState(1); // 1 = mañana
  const day = React.useMemo(() => dayjs().add(dayOffset, 'day').startOf('day'), [dayOffset]);
  const dayStr = fmtDate(day);

  // Traemos solo ese día (igual que se hace con "hoy")
  const { rows = [], loading } = useReservas(dayStr, dayStr, {
    profesorId,
  } as any);

  const count = React.useMemo(
    () =>
      rows
        .filter((r) => !isCancelled(r))
        .map((r) => getStart(r))
        .filter((s) => (s ? s.isSame(day, 'day') : false)).length,
    [rows, day],
  );

  const label =
    dayOffset === 1
      ? String(t('profesor.dashboard.kpiManana', 'Clases mañana'))
      : String(
          t('profesor.dashboard.kpiClasesFecha', 'Clases {{d}}', {
            d: day.format('D MMM'),
          }),
        );

  return (
    <KpiCard
      icon={<Schedule />}
      label={label}
      value={count}
      loading={loading}
      action={
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            aria-label={String(t('profesor.dashboard.diaAnterior', 'Día anterior'))}
            onClick={() => setDayOffset((v) => Math.max(1, v - 1))}
            disabled={dayOffset <= 1}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            size="small"
            aria-label={String(t('profesor.dashboard.diaSiguiente', 'Día siguiente'))}
            onClick={() => setDayOffset((v) => v + 1)}
          >
            <ChevronRight />
          </IconButton>
        </Stack>
      }
    />
  );
}

/* ========= NUEVO: KPI de Horas semanales con navegación (ya existente) ========= */
function HorasSemanaKpi() {
  const { t } = useTranslation<'translation'>();
  const { user } = useAuth();
  const profesorId = user?.id;

  const [weekOffset, setWeekOffset] = React.useState(0); // 0 = semana actual
  const weekStart = React.useMemo(
    () => startOfISOWeek(dayjs()).add(weekOffset, 'week'),
    [weekOffset],
  );
  const weekEnd = React.useMemo(() => weekStart.add(6, 'day').endOf('day'), [weekStart]);

  const { rows = [], loading } = useReservas(fmtDate(weekStart), fmtDate(weekEnd), {
    profesorId,
  } as any);

  const horas = React.useMemo(() => {
    return rows
      .filter((r) => {
        const s = getStart(r);
        return s ? s.isBetween(weekStart, weekEnd, 'day', '[]') : false;
      })
      .filter(countsInTeachingHours)
      .reduce((acc: number, r: any) => acc + durHours(r), 0);
  }, [rows, weekStart, weekEnd]);

  const labelSemana = `${weekStart.format('D MMM')} – ${weekEnd.format('D MMM')}`;

  return (
    <Card elevation={0} variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ mb: 1 }}
        >
          <IconButton
            size="small"
            aria-label={String(t('profesor.dashboard.semanaAnterior', 'Semana anterior'))}
            onClick={() => setWeekOffset((v) => v - 1)}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {labelSemana}
          </Typography>
          <IconButton
            size="small"
            aria-label={String(t('profesor.dashboard.semanaSiguiente', 'Semana siguiente'))}
            onClick={() => setWeekOffset((v) => v + 1)}
          >
            <ChevronRight />
          </IconButton>
        </Stack>

        {loading ? (
          <Skeleton height={32} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {horas.toFixed(1)}{' '}
            <Typography component="span" color="text.secondary">
              h
            </Typography>
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/* ====================== Página ====================== */
export default function ProfesorDashboardPage() {
  const { t } = useTranslation<'translation'>();
  const { rowsHoy, proximas, pendingCounts, loading } = useProfesorDashboardData();

  // ⬇️ saludo personalizado
  const { user } = useAuth();
  const [myUserData, setMyUserData] = React.useState<{ nombreCompleto?: string } | null>(null);

  // Obtener datos del usuario actual desde /usuarios/me (sin cargar todos los usuarios)
  React.useEffect(() => {
    if (!user?.id) return;
    api.get('/usuarios/me')
      .then((res) => setMyUserData(res.data))
      .catch(() => setMyUserData(null)); // En caso de error, usar email
  }, [user?.id]);

  const displayName = React.useMemo(() => {
    return myUserData?.nombreCompleto || (user?.email ?? '');
  }, [myUserData, user?.email]);

  const firstName = React.useMemo(() => {
    const base = displayName.includes('@')
      ? displayName.split('@')[0].replace(/[._-]+/g, ' ')
      : displayName;
    const parts = base.trim().split(/\s+/);
    const first = parts[0] || 'Profesor';
    return first.charAt(0).toUpperCase() + first.slice(1);
  }, [displayName]);

  const hour = dayjs().hour();
  const saludo = hour < 12 ? t('buenos.dias') : hour < 20 ? t('buenos.tardes') : t('buenos.noches');

  const [selectedReserva, setSelectedReserva] = React.useState<any | null>(null);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1, color: 'inherit' }}>
            {`${saludo}, ${firstName}`}
          </Typography>

          <Button
            variant="outlined"
            startIcon={<CalendarMonth />}
            component={RouterLink}
            to="/profesor/guardias"
            sx={{
              color: '#21a598',
              borderColor: '#21a598',
              '&:hover': {
                backgroundColor: 'rgba(33,165,152,0.08)',
                borderColor: '#1b887f',
                color: '#1b887f',
              },
            }}
          >
            {t('profesor.dashboard.verGuardias')}
          </Button>
        </Stack>

        {/* KPIs */}
        <Grid container spacing={2} columns={{ xs: 12, md: 12 }} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%' }}>
              <KpiCard
                icon={<PendingActions />}
                label={t('profesor.dashboard.kpiPendientes')}
                value={pendingCounts?.total ?? 0}
                loading={loading.counts}
                action={
                  <Button
                    size="small"
                    component={RouterLink}
                    to="/profesor/firmas"
                    variant="contained"
                  >
                    {t('profesor.dashboard.firmar')}
                  </Button>
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ height: '100%' }}>
              <ClasesDiaKpi />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ height: '100%' }}>
              <HorasSemanaKpi />
            </Box>
          </Grid>
        </Grid>

        {/* Listas y widgets */}
        <Grid container spacing={2}>
          {/* IZQUIERDA: Clases hoy */}
          <Grid item xs={12} md={6}>
            <ClasesHoyList
              rows={rowsHoy}
              loading={loading.hoy}
              onOpenDetails={(r) => setSelectedReserva(r)}
            />
          </Grid>

          {/* DERECHA: Calendario + horas + progresión DEBAJO */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* calendario + horas como estaban */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                  gap: 2,
                  alignItems: 'stretch', 
                }}
              >
                <Box>
                  <CalendarClasesCard />
                </Box>

                <SemanaHorasKPIs />
              </Box>

              {/* AQUÍ la progresión — siempre debajo — mismo ancho */}
              <ClasesProgressionCard />
            </Box>
          </Grid>

          {/* Debajo de todo: Próximas */}
          <Grid item xs={12}>
            <ProximasClasesAccordion
              rows={proximas}
              loading={loading.proximas}
              onOpenDetails={(r) => setSelectedReserva(r)}
            />
          </Grid>
        </Grid>

        {selectedReserva && (
          <ReservaDialog open reserva={selectedReserva} onClose={() => setSelectedReserva(null)} />
        )}
      </Box>
    </ThemeProvider>
  );
}
